import { apiFetch } from "./api.js";

// Cross-tab signal for tenant selection. The session cookie is browser-wide,
// so selecting a tenant in one tab silently flips every other tab's
// server-side context; this channel tells those tabs immediately instead of
// letting them find out on their next (rejected) request. The hub-side
// x-atlas-tenant-id guard remains the enforcement — this is only the prompt
// notice.
const CHANNEL_NAME = "atlas.tenant.selected";

export type TenantSelectedMessage = { tenant_id: string; tenant_name: string };

export async function selectTenant(tenantId: string, tenantName: string): Promise<void> {
  await apiFetch<{ ok: true }>("/api/auth/select-tenant", {
    method: "POST",
    body: { tenant_id: tenantId }
  });
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage({ tenant_id: tenantId, tenant_name: tenantName } satisfies TenantSelectedMessage);
    channel.close();
  } catch {
    // BroadcastChannel unavailable — other tabs still get stopped by the
    // hub-side guard on their next request.
  }
}

/** Subscribe to tenant selections made in OTHER tabs. Returns an unsubscribe. */
export function subscribeTenantSelected(onSelected: (msg: TenantSelectedMessage) => void): () => void {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      const data = e.data as Partial<TenantSelectedMessage> | null;
      if (data && typeof data.tenant_id === "string") {
        onSelected({ tenant_id: data.tenant_id, tenant_name: typeof data.tenant_name === "string" ? data.tenant_name : "" });
      }
    };
    return () => channel.close();
  } catch {
    return () => {};
  }
}
